/**
 * Мінімальний трекер відвідувань: жодних кукі, зовнішніх скриптів і персональних
 * даних — лише тип події, яка йде на власний бекенд (/api/track).
 */

const ENDPOINT = '/api/track';

/** Пререндер (headless Chrome) не має потрапляти у статистику. */
const isRealVisitor = () =>
  typeof window !== 'undefined' && !navigator.webdriver && window.location.protocol.startsWith('http');

function send(type, detail) {
  if (!isRealVisitor()) return;

  // Адреса сторінки їде разом із подією. Без неї подія «перегляд» не каже, що
  // саме переглянули, і відновити шлях відвідувача сайтом неможливо — а це
  // єдине, заради чого лічильник на такому трафіку взагалі потрібен.
  const payload = JSON.stringify({
    type,
    detail,
    page: window.location.pathname,
    referrer: document.referrer || undefined
  });

  // sendBeacon переживає закриття вкладки; fetch — запасний варіант.
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: 'application/json' }));
      return;
    }
  } catch {
    // ігноруємо — статистика не має ламати сайт
  }

  fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true
  }).catch(() => {});
}

/**
 * Перегляд сторінки.
 *
 * Раніше тут стояв прапорець «уже надіслано» на весь час життя вкладки, і
 * через це переходи всередині сайту не рахувалися зовсім: людина відкривала
 * головну, гортала п'ять сторінок послуг — у статистиці лишався один перегляд.
 * Тобто «сторінок на особу» насправді означало «перезавантажень на особу».
 *
 * Тепер рахується кожен перехід. Захист лишається, але вузький: та сама адреса
 * протягом двох секунд — це подвійний виклик ефекту, а не другий перегляд.
 */
let lastPath = null;
let lastAt = 0;

export function trackPageview() {
  const path = window.location.pathname;
  const now = Date.now();
  if (path === lastPath && now - lastAt < 2000) return;
  lastPath = path;
  lastAt = now;
  send('pageview');
}

export function trackEvent(type, detail) {
  send(type, detail);
}

/**
 * Куди натискали.
 *
 * Один обробник на весь документ замість позначок у двадцяти компонентах:
 * посилання на сайті й так описують себе адресою, тож підпис виводиться з
 * неї. Нові посилання потрапляють у статистику самі, без жодних правок.
 *
 * Слухаємо на етапі занурення (capture) — щоб порахувати клік навіть тоді,
 * коли обробник самого посилання зупинить подію.
 */
const linkLabel = (el) => {
  const href = el.getAttribute('href') || '';
  if (href.startsWith('tel:')) return 'телефон';
  if (href.startsWith('mailto:')) return 'пошта';
  if (href.startsWith('viber:')) return 'viber';
  if (/^https?:\/\/t\.me/i.test(href)) return 'telegram';

  try {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return `зовні: ${url.hostname}`;
    return url.pathname + (url.hash || '');
  } catch {
    return href.slice(0, 40);
  }
};

let clicksBound = false;

export function trackClicks() {
  if (clicksBound || !isRealVisitor()) return () => {};
  clicksBound = true;

  const onClick = (event) => {
    const link = event.target.closest?.('a[href]');
    if (!link) return;
    send('link_click', linkLabel(link));
  };

  document.addEventListener('click', onClick, { capture: true });
  return () => {
    document.removeEventListener('click', onClick, { capture: true });
    clicksBound = false;
  };
}

/**
 * Позначає, до яких блоків відвідувач реально доскролив. Показує, що читають,
 * а що пропускають. Кожен блок рахується один раз за візит.
 *
 * Лічильник спрацьовує, коли блок перетинає середню третину екрана, а не коли
 * видно 35% його висоти. Різниця не косметична: поріг у частках висоти
 * недосяжний для високого блоку. «Послуги» на телефоні мають 3162px при екрані
 * 844px — це максимум 27% видимості, тобто блок не міг зарахуватися ніколи.
 * У панелі це виглядало так, ніби до «Послуг» доскролило менше людей (20%),
 * ніж до «Переваг» (35%), які лежать нижче, — хоча пройти повз «Послуги» до
 * них неможливо.
 *
 * Середня третина екрана працює однаково для блоку будь-якої висоти: і
 * високий, і низький через неї проходять.
 */
export function trackSections() {
  if (!isRealVisitor() || typeof IntersectionObserver === 'undefined') return () => {};

  const reported = new Set();
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting || reported.has(entry.target.id)) continue;
        reported.add(entry.target.id);
        send('section_view', entry.target.id);
        observer.unobserve(entry.target);
      }
    },
    { threshold: 0, rootMargin: '-33% 0px -33% 0px' }
  );

  document.querySelectorAll('section[id]').forEach((section) => observer.observe(section));
  return () => observer.disconnect();
}
